export const USERS_ADMIN_PERMISSION = "users:admin";
export const USERS_READ_PERMISSION = "users:read";
export const BODYCAM_MANAGE_PERMISSION = "bodycam:manage";
export const API_KEYS_MANAGE_PERMISSION = "api_keys:manage";
export const WIDGETS_API_KEYS_MANAGE_PERMISSION = "widgets:api-keys:manage";
export const REPORTS_MANAGE_STATION_PERMISSION = "reports:manage_station";
export const EVIDENCE_EDIT_OWN_PERMISSION = "evidence:edit_own";
export const EVIDENCE_EDIT_STATION_PERMISSION = "evidence:edit_station";
export const EVIDENCE_READ_PERMISSION = "evidence:read";
export const EVIDENCE_READ_STATION_PERMISSION = "evidence:read_station";
export const EVIDENCE_UPLOAD_PERMISSION = "evidence:upload";
export const TRANSCRIPT_DOWNLOAD_PERMISSION = "transcript:download";
export const TRANSCRIPT_GENERATE_PERMISSION = "transcript:generate";

// WorkOS role slugs. These match the RBAC slugs defined in
// core-cdk (packages/infra/lib/workosDesiredState.ts): a station-scoped admin
// gets "station_admin"; internal KARDA staff get "org-karda-admin".
export const STATION_ADMIN_ROLE = "station_admin";
export const KARDA_ADMIN_ROLE = "org-karda-admin";

/** True when the signed-in user is a station-scoped admin. useAuth().role is a
 *  single slug string, so this is an exact compare (mirrors SideBar's
 *  hasKardaAdminRole check). */
export function isStationAdmin(role: string | null | undefined): boolean {
  return role === STATION_ADMIN_ROLE;
}

export function canManageUsers(permissions: string[]): boolean {
  return permissions.includes(USERS_ADMIN_PERMISSION);
}

export function canManageBodycam(permissions: string[]): boolean {
  return permissions.includes(BODYCAM_MANAGE_PERMISSION);
}

export function canReadUsers(permissions: string[]): boolean {
  // For now, we assume that if you can manage users, you can also read them.
  return permissions.includes(USERS_READ_PERMISSION) || canManageUsers(permissions);
}

export function canManageWidgetApiKeys(permissions: string[]): boolean {
  return permissions.includes(WIDGETS_API_KEYS_MANAGE_PERMISSION);
}

export function canReviewReports(permissions: string[]): boolean {
  return permissions.includes(REPORTS_MANAGE_STATION_PERMISSION);
}

/**
 * Mirrors the Evidence API's share/revoke/list gate: the video's uploader or
 * anyone holding evidence:read_station may manage a video's shares.
 */
export function canManageEvidenceShares(
  permissions: string[],
  isUploader: boolean,
): boolean {
  return isUploader || permissions.includes(EVIDENCE_READ_STATION_PERMISSION);
}

/**
 * Can this user put media into the evidence locker?
 *
 * Load-bearing for INCIDENTS, not just the evidence page: video and audio
 * attached to an incident are uploaded to the evidence service, so an officer
 * without this permission cannot add a clip to an incident at all. The upload
 * API enforces it and answers 403 — mid-upload, after the file has been picked —
 * so the UI checks first and says so up front.
 */
export function canUploadEvidence(permissions: string[]): boolean {
  return permissions.includes(EVIDENCE_UPLOAD_PERMISSION);
}

export function canEditEvidence(permissions: string[]): boolean {
  return (
    permissions.includes(EVIDENCE_EDIT_OWN_PERMISSION) ||
    permissions.includes(EVIDENCE_EDIT_STATION_PERMISSION)
  );
}

/**
 * Per-video edit gate. Editing your own upload requires evidence:edit_own (or
 * the broader evidence:edit_station); editing someone else's upload requires
 * evidence:edit_station. Mirrors canManageEvidenceShares' isUploader signature.
 */
export function canEditEvidenceItem(
  permissions: string[],
  isUploader: boolean,
): boolean {
  if (isUploader) {
    return (
      permissions.includes(EVIDENCE_EDIT_OWN_PERMISSION) ||
      permissions.includes(EVIDENCE_EDIT_STATION_PERMISSION)
    );
  }
  return permissions.includes(EVIDENCE_EDIT_STATION_PERMISSION);
}

export function canDownloadTranscript(permissions: string[]): boolean {
  return permissions.includes(TRANSCRIPT_DOWNLOAD_PERMISSION);
}

export function canGenerateTranscript(permissions: string[]): boolean {
  return permissions.includes(TRANSCRIPT_GENERATE_PERMISSION);
}
